/**
 * Exchanges action toolbar filter → PBHOrdersSQLOnly OData `$filter` (DE-9312).
 *
 * OData `MaxNodeCount` on the API is 100 — full action-bucket expressions exceed
 * that limit. These clauses are **broad prefilters** only; `applyActionFilterToRows`
 * in `api.service.js` refines results client-side. Waiting OData prefilters
 * exclude the opposite side's Raw* set (internal vs external) so chunks align
 * with client bucket rules. List pagination fetches OData chunks until the
 * current UI page is filled (`pbhApi.ts`).
 */

export const EXCHANGES_ACTION_FILTER_VALUES = [
  "Action",
  "Waiting",
  "Open",
  "Closed",
  "Cancelled",
] as const;

export type ExchangesActionFilterValue =
  (typeof EXCHANGES_ACTION_FILTER_VALUES)[number];

export interface SqlOnlyActionODataFilter {
  clause: string;
}

/** Broad prefilter always needs client-side `applyActionFilterToRows`. */
export const SQL_ONLY_ACTION_USES_CLIENT_REFINEMENT = true;

const AJW_COMPANY_ID = 2;

const joinOr = (clauses: string[]): string => clauses.join(" or ");

/**
 * Resolve company id for action buckets (matches `isInternalAjwUser` — root or
 * nested `company.id`, loose `== 2` for internal).
 */
export const resolveActionFilterCompanyId = (
  user?: Record<string, unknown> | null,
  companyId?: number | string | null,
): number | undefined => {
  if (user && typeof user === "object") {
    const rootId = user.companyId;
    const nestedId = (user.company as { id?: unknown } | undefined)?.id;
    if (rootId == AJW_COMPANY_ID || nestedId == AJW_COMPANY_ID) {
      return AJW_COMPANY_ID;
    }
    const fromUser = Number(rootId ?? nestedId);
    if (Number.isFinite(fromUser)) {
      return fromUser;
    }
  }
  if (companyId == null || companyId === "") {
    return undefined;
  }
  const parsed = Number(companyId);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const isInternalActionFilterUser = (
  user?: Record<string, unknown> | null,
  companyId?: number | string | null,
): boolean =>
  resolveActionFilterCompanyId(user, companyId) === AJW_COMPANY_ID;

/**
 * Outbound Raw* values that may map to customer-waiting statuses
 * (`ajwWaiting` for internal users).
 */
const buildOutboundCustomerWaitingPrefilter = (): string =>
  joinOr([
    "RawOutboundDiscriminator eq null",
    "RawOutboundDiscriminator eq 'AcceptDeclineAction'",
    "RawStatus eq 'ExchangeRequestAction'",
    "RawStatus eq 'CoveredAction'",
    "RawStatus eq 'InStockAction'",
  ]);

/**
 * Inbound Raw* values that may map to AJW-waiting statuses
 * (`customerWating` for internal users).
 */
const buildInboundAjwWaitingPrefilter = (): string =>
  joinOr([
    "RawInboundDiscriminator eq null",
    "RawInboundDiscriminator eq 'OffcorePartAction'",
    "RawInboundDiscriminator eq 'OffcoreDetailAction'",
  ]);

const buildCancelledPrefilter = (): string =>
  "RawStatus eq 'CancellationAction'";

const buildClosedPrefilter = (): string =>
  joinOr([
    "RawStatus eq 'CancellationAction'",
    "RawStatus eq 'ShipmentAction'",
  ]);

const buildOpenPrefilter = (): string =>
  `not (RawStatus eq 'CancellationAction' or RawStatus eq 'ShipmentAction')`;

const negateODataClause = (clause: string): string => `not (${clause})`;

/**
 * Internal Waiting: inbound may need AJW action AND outbound must not be in the
 * customer-waiting set (else both `customerWating` and `ajwWaiting` are set).
 */
const buildInternalWaitingPrefilter = (): string => {
  const clause = combineSqlOnlyODataFilterClauses(
    buildInboundAjwWaitingPrefilter(),
    negateODataClause(buildOutboundCustomerWaitingPrefilter()),
  );
  return clause ?? buildInboundAjwWaitingPrefilter();
};

/**
 * External Waiting: outbound may need customer action AND inbound must not be in
 * the AJW-waiting set (else both flags are set).
 */
const buildExternalWaitingPrefilter = (): string => {
  const clause = combineSqlOnlyODataFilterClauses(
    buildOutboundCustomerWaitingPrefilter(),
    negateODataClause(buildInboundAjwWaitingPrefilter()),
  );
  return clause ?? buildOutboundCustomerWaitingPrefilter();
};

/** Combine multiple OData filter fragments with AND. */
export const combineSqlOnlyODataFilterClauses = (
  ...clauses: Array<string | null | undefined>
): string | undefined => {
  const normalized = clauses
    .map((clause) => clause?.trim())
    .filter((clause): clause is string => Boolean(clause));

  if (normalized.length === 0) {
    return undefined;
  }
  if (normalized.length === 1) {
    return normalized[0];
  }
  return normalized.map((clause) => `(${clause})`).join(" and ");
};

const resolveCompanyId = (
  userOrCompanyId?: Record<string, unknown> | number | string | null,
): number | undefined => {
  if (userOrCompanyId == null) {
    return undefined;
  }
  if (typeof userOrCompanyId === "object") {
    return resolveActionFilterCompanyId(userOrCompanyId);
  }
  return resolveActionFilterCompanyId(undefined, userOrCompanyId);
};

/**
 * Broad OData prefilter for an action toolbar value.
 * Client-side refinement is always required (`SQL_ONLY_ACTION_USES_CLIENT_REFINEMENT`).
 */
export const getSqlOnlyActionODataFilter = (
  action: string | null | undefined,
  userOrCompanyId?: Record<string, unknown> | number | string | null,
): SqlOnlyActionODataFilter | undefined => {
  const normalized = String(action || "").trim();
  if (!normalized) {
    return undefined;
  }

  const resolvedCompanyId = resolveCompanyId(userOrCompanyId);
  const isInternal = resolvedCompanyId === AJW_COMPANY_ID;

  switch (normalized as ExchangesActionFilterValue) {
    case "Cancelled":
      return { clause: buildCancelledPrefilter() };
    case "Closed":
      return { clause: buildClosedPrefilter() };
    case "Open":
      return { clause: buildOpenPrefilter() };
    case "Action":
      return {
        clause: isInternal
          ? buildOutboundCustomerWaitingPrefilter()
          : buildInboundAjwWaitingPrefilter(),
      };
    case "Waiting":
      return {
        clause: isInternal
          ? buildInternalWaitingPrefilter()
          : buildExternalWaitingPrefilter(),
      };
    default:
      return undefined;
  }
};

/** Whether a broad OData prefilter exists for this action (client refinement still runs). */
export const supportsSqlOnlyActionODataFilter = (
  action: string | null | undefined,
): boolean => getSqlOnlyActionODataFilter(action) !== undefined;
